import React from 'react'
import { Button } from '@/components/ui/button'
import { ArrowRight, Edit3, Sparkles, FileText } from 'lucide-react'
import { Helmet } from 'react-helmet'
import { trackEvent, EVENTS } from '@/lib/analytics'
import SiteHeader from '@/components/SiteHeader'
import SiteFooter from '@/components/SiteFooter'

// BragBox landing page, decluttered 2026-08-08 (maintenance-mode reset).
// One mission: help professionals capture wins as they happen and walk into
// review meetings with polished, review-ready evidence.
// One primary CTA (Start free). One quiet secondary (Log in). No offer grid,
// no supporting-link stack, no paid-shortcut routing, no marketplace.

export default function Landing() {
  const goTo = (path) => {
    window.location.assign(path)
  }

  const handleStartFree = (location = 'landing_hero') => {
    trackEvent(EVENTS.SIGNUP_STARTED, { source: location, cta: 'start_free', method: 'cta_click' })
    goTo('/login?signup=true')
  }

  const handleLogIn = () => {
    goTo('/login')
  }

  const howItWorks = [
    {
      icon: <Edit3 className="h-6 w-6" style={{ color: '#1F2A44' }} />,
      title: 'Capture',
      body: 'Type a win in your own words as it happens. Twenty seconds is enough. Voice input works too.'
    },
    {
      icon: <Sparkles className="h-6 w-6" style={{ color: '#1F2A44' }} />,
      title: 'Polish',
      body: 'The AI rewrites your raw text in the language your manager respects. STAR structure. Numbers where they belong.'
    },
    {
      icon: <FileText className="h-6 w-6" style={{ color: '#1F2A44' }} />,
      title: 'Prep',
      body: 'Tag each win with a review objective. Export a clean review packet when the conversation is coming up.'
    }
  ]

  return (
    <div className="min-h-screen bg-white font-sans">
      <Helmet>
        <title>BragBox: capture your wins, prep for your review</title>
        <meta name="description" content="A small workspace for professionals who forget half their best work by review season. Capture a win in twenty seconds, let the AI polish it into review-ready language, and export a clean review packet when your manager asks for one." />
        <link rel="canonical" href="https://bragbox.ai/" />
        <meta property="og:title" content="BragBox: capture your wins, prep for your review" />
        <meta property="og:description" content="Capture wins as they happen. Let the AI polish them into review-ready language. Walk into your next review with the receipts already written." />
        <meta property="og:url" content="https://bragbox.ai/" />
        <meta property="og:image" content="https://bragbox.ai/assets/og-default.png" />
        <meta name="twitter:title" content="BragBox: capture your wins, prep for your review" />
        <meta name="twitter:description" content="Capture wins as they happen. Let the AI polish them into review-ready language." />
      </Helmet>

      <SiteHeader source="landing_nav" />

      {/* Hero */}
      <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-20 md:py-28 text-center">
        <p className="text-xs font-bold tracking-widest uppercase mb-4" style={{ color: '#C4A35A' }}>
          BUILT FOR YOUR NEXT REVIEW
        </p>
        <h1 className="text-4xl md:text-6xl font-bold leading-tight mb-6" style={{ color: '#1F2A44', fontFamily: 'Manrope' }}>
          Save your wins as they happen. Walk into your review with the receipts already written.
        </h1>
        <p className="text-lg md:text-xl text-gray-600 max-w-2xl mx-auto mb-10">
          BragBox is a small workspace for professionals who forget half their best work by review season. Capture a win in twenty seconds. Let the AI polish it. Show up ready.
        </p>

        <div className="flex flex-col items-center gap-3 mb-6 w-full">
          <Button
            size="lg"
            className="text-base px-10 py-4 h-auto shadow-md"
            style={{ backgroundColor: '#1F2A44', color: 'white' }}
            onClick={() => handleStartFree('landing_hero')}
          >
            Start free <ArrowRight className="ml-2 h-5 w-5" />
          </Button>
          <button
            onClick={handleLogIn}
            className="text-sm font-medium underline underline-offset-4"
            style={{ color: '#1F2A44' }}
          >
            Already have an account? Log in.
          </button>
        </div>

        <p className="text-sm text-gray-500 max-w-2xl mx-auto mt-4">
          Free to start. No credit card at signup. Your data belongs to you.
        </p>
      </section>

      {/* How it works */}
      <section className="py-20 max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <p className="text-xs font-bold tracking-widest uppercase text-center mb-4" style={{ color: '#C4A35A' }}>
          HOW BRAGBOX WORKS
        </p>
        <h2 className="text-2xl md:text-3xl font-bold text-center mb-12" style={{ color: '#1F2A44' }}>
          Three steps. That is the whole product.
        </h2>
        <div className="grid md:grid-cols-3 gap-6">
          {howItWorks.map((step, i) => (
            <div key={step.title} className="bg-white border border-gray-100 rounded-2xl p-6 shadow-sm">
              <div className="flex items-center gap-3 mb-3">
                <div className="rounded-lg p-2" style={{ backgroundColor: '#fdf6e3' }}>
                  {step.icon}
                </div>
                <span className="text-xs font-bold tracking-widest uppercase" style={{ color: '#C4A35A' }}>
                  Step {i + 1}
                </span>
              </div>
              <h3 className="text-lg font-bold mb-2" style={{ color: '#1F2A44' }}>{step.title}</h3>
              <p className="text-gray-600 text-sm leading-relaxed">{step.body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Before / after example */}
      <section className="bg-gray-50 py-16">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-xs font-bold tracking-widest uppercase text-center mb-3" style={{ color: '#1F2A44' }}>
            THE DIFFERENCE THE POLISH MAKES
          </p>
          <h2 className="text-2xl md:text-3xl font-bold text-center mb-10" style={{ color: '#1F2A44' }}>
            Most people do solid work. Then describe it too vaguely to matter.
          </h2>
          <div className="grid md:grid-cols-2 gap-6">
            <div className="border border-gray-200 rounded-2xl p-6 bg-white shadow-sm">
              <div className="flex items-center gap-2 mb-3">
                <span className="h-3 w-3 rounded-full bg-red-400 inline-block"></span>
                <span className="text-xs font-bold tracking-wider text-gray-500 uppercase">Raw win</span>
              </div>
              <p className="text-gray-700 text-base italic">
                "Helped fix the login bug that was causing issues for users"
              </p>
              <p className="text-xs text-gray-400 mt-3">Vague. Forgettable. Easy to skip in a review.</p>
            </div>
            <div className="border-2 rounded-2xl p-6 shadow-sm" style={{ borderColor: '#C4A35A', backgroundColor: '#fdf6e3' }}>
              <div className="flex items-center gap-2 mb-3">
                <span className="h-3 w-3 rounded-full bg-green-500 inline-block"></span>
                <span className="text-xs font-bold tracking-wider uppercase" style={{ color: '#1F2A44' }}>Polished by BragBox</span>
              </div>
              <p className="text-gray-800 text-base italic">
                "Fixed the auth bug blocking 12% of logins. Cut failure rate by 94%, unblocked 340 users, and gave support a clear explanation to share with customers."
              </p>
              <p className="text-xs mt-3" style={{ color: '#1F2A44' }}>Specific. Evidenced. Hard to argue with.</p>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16 max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        <h2 className="text-2xl md:text-3xl font-bold text-center mb-10" style={{ color: '#1F2A44' }}>
          Common questions
        </h2>
        <div className="space-y-4">
          <details className="border border-gray-200 rounded-xl p-5 group">
            <summary className="font-semibold cursor-pointer list-none flex justify-between items-center" style={{ color: '#1F2A44' }}>
              Is my data private?
              <span className="text-gray-400 group-open:rotate-180 transition-all">▾</span>
            </summary>
            <p className="mt-3 text-gray-600 text-sm">
              Yes. Your data is encrypted at rest and in transit. We never sell it, we never share it with recruiters, and we never use it to train AI models. Your career record belongs to you, not your current employer, and it travels with you when you leave.
            </p>
          </details>
          <details className="border border-gray-200 rounded-xl p-5 group">
            <summary className="font-semibold cursor-pointer list-none flex justify-between items-center" style={{ color: '#1F2A44' }}>
              How is this different from a Notion doc?
              <span className="text-gray-400 group-open:rotate-180 transition-all">▾</span>
            </summary>
            <p className="mt-3 text-gray-600 text-sm">
              A blank page will not rewrite your work into review-ready language. BragBox polishes each win the second you save it, links it to the review objective you care about, and exports a clean packet when the conversation is coming up. A notes app is a filing cabinet. This is a workspace.
            </p>
          </details>
          <details className="border border-gray-200 rounded-xl p-5 group">
            <summary className="font-semibold cursor-pointer list-none flex justify-between items-center" style={{ color: '#1F2A44' }}>
              What does it cost?
              <span className="text-gray-400 group-open:rotate-180 transition-all">▾</span>
            </summary>
            <p className="mt-3 text-gray-600 text-sm">
              The app is free to start and does not ask for a credit card at signup.
            </p>
          </details>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20" style={{ backgroundColor: '#1F2A44' }}>
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4" style={{ fontFamily: 'Manrope' }}>
            Your review will not reward work nobody can remember.
          </h2>
          <p className="text-base mb-8 max-w-xl mx-auto" style={{ color: '#96A3BC' }}>
            Start capturing today so the next one goes differently.
          </p>
          <Button
            size="lg"
            className="font-semibold px-10 py-4 h-auto text-base"
            style={{ backgroundColor: '#C4A35A', color: '#1F2A44' }}
            onClick={() => handleStartFree('landing_final')}
          >
            Start free <ArrowRight className="ml-2 h-5 w-5" />
          </Button>
          <p className="text-gray-300 text-sm mt-4">Free to start. No credit card.</p>
        </div>
      </section>

      <SiteFooter />
    </div>
  )
}
